import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import axios, { CancelTokenSource } from 'axios';
import Skeleton from 'react-loading-skeleton';

import { APPLOG_BASE_URL } from 'constants/url';
import { APP_LOG_ACTION_LABELS, APP_LOG_ENTITY_LABELS, APP_LOG_SOURCE_LABELS } from 'constants/appLog';
import ChangesTable from './ChangesTable';

interface Props {
  appLogId: number;
  onFilterRequest: (requestId: string) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.default
  },
  section: {
    marginTop: theme.spacing(2)
  },
  label: {
    color: theme.palette.text.secondary
  },
  value: {
    wordBreak: 'break-word'
  }
}));

const AppLogDetail: FC<Props> = props => {
  const classes = useStyles();
  const { appLogId, onFilterRequest } = props;

  const [appLog, setAppLog] = useState<AppLogModel>();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isError, setError] = useState<boolean>(false);

  useEffect(() => {
    const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

    const loadDetail = async () => {
      setLoading(true);
      setError(false);

      try {
        const { data } = await axios.get(`${APPLOG_BASE_URL}/${appLogId}`, { cancelToken: cancelTokenSource.token });
        setAppLog(data);
        setLoading(false);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setError(true);
          setLoading(false);
        }
      }
    };

    loadDetail();

    return () => {
      cancelTokenSource.cancel();
    };
  }, [appLogId]);

  const renderInfo = (label: string, value: ReactNode) => (
    <Grid item xs={12} sm={6} md={4} key={label}>
      <Typography variant='caption' className={classes.label}>
        {label}
      </Typography>
      <Typography variant='body2' className={classes.value}>
        {value || '-'}
      </Typography>
    </Grid>
  );

  if (isLoading) {
    return (
      <div className={classes.container}>
        <Skeleton count={3} />
      </div>
    );
  }

  if (isError || !appLog) {
    return (
      <div className={classes.container}>
        <Typography variant='body2' color='error'>
          Failed to load the log detail
        </Typography>
      </div>
    );
  }

  const { source, action, entityType, entityId, entityLabel, ipAddress, userAgent, requestId, changes, metadata } = appLog;

  return (
    <div className={classes.container}>
      <Grid container spacing={2}>
        {renderInfo('Source', source && (APP_LOG_SOURCE_LABELS[source] || source))}
        {renderInfo('Action', action && (APP_LOG_ACTION_LABELS[action] || action))}
        {renderInfo(
          'Record',
          entityType &&
            `${APP_LOG_ENTITY_LABELS[entityType] || entityType} ${[entityLabel, entityId ? `(#${entityId})` : ''].filter(Boolean).join(' ')}`
        )}
        {renderInfo('IP Address', ipAddress)}
        {renderInfo('Device', userAgent)}
        {renderInfo(
          'Request',
          requestId && (
            <Button size='small' color='primary' style={{ padding: 0 }} onClick={() => onFilterRequest(requestId)}>
              Show all logs from this action
            </Button>
          )
        )}
      </Grid>
      {metadata && Object.keys(metadata).length > 0 && (
        <div className={classes.section}>
          <Typography variant='subtitle2' gutterBottom>
            Additional Info
          </Typography>
          <Grid container spacing={2}>
            {Object.keys(metadata).map(key =>
              renderInfo(key, typeof metadata[key] === 'object' ? JSON.stringify(metadata[key]) : String(metadata[key]))
            )}
          </Grid>
        </div>
      )}
      {changes && (
        <div className={classes.section}>
          <Typography variant='subtitle2' gutterBottom>
            Changes
          </Typography>
          <ChangesTable changes={changes} />
        </div>
      )}
    </div>
  );
};

export default AppLogDetail;
